import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import PublicPlatformQrCard from '../../public/PublicPlatformQrCard';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import { canSeeDashboardModule } from '../../../constants/adminModules';
import { formatMoney } from '../../../utils/currency';

type DashboardUser = {
  role?: string;
  adminModules?: string[];
};

type RailData = {
  inscriptionsTotal?: number;
  inscriptionsEnAttentePaiement: number;
  inscriptionsConfirmees: number;
  inscriptionsPartiellementPayees?: number;
  apprenantsTotal?: number;
  cohortesOuvertes?: number;
  cohortesPleines?: number;
  montantPayeCdf: number;
  montantTotalCdf: number;
  paiements?: {
    recettesJournalieres?: number;
    montantImpayeTotal?: number;
    paiementsEnAttente?: number;
    bonusDistribuesMois?: number;
  };
  module10?: {
    academique?: {
      nombreApprenants?: number;
      tauxReussitePct?: number | null;
      inscriptionsReussies?: number;
      classementGeneralTotal?: number;
      classementShortlist?: number;
      tailleShortlist?: number;
    };
    financier?: {
      recettesJournalieres?: number;
      montantImpayeTotal?: number;
      paiementsEnAttente?: number;
      bonusDistribuesMois?: number;
    };
    rh?: {
      heuresPresteesMois?: number;
      remunerationsDuCdf?: number;
      remunerationsPayeesCdf?: number;
      tauxSatisfactionPedagogiquePct?: number | null;
      nombreEvaluations?: number;
    };
    logistique?: {
      totalArticles?: number;
      equipementsDisponibles?: number;
      equipementsEnPanne?: number;
      pannesOuvertes?: number;
    };
  };
};

type Props = {
  user?: DashboardUser;
  data: RailData;
};

function RailCard({
  title,
  children,
  footer,
}: {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white p-6 shadow-sm">
      <h3 className="mb-4 text-lg font-bold">{title}</h3>
      {children}
      {footer ? <div className="mt-6">{footer}</div> : null}
    </div>
  );
}

function RailActionLink({
  to,
  label,
  icon,
  variant = 'outline',
}: {
  to: string;
  label: string;
  icon: string;
  variant?: 'primary' | 'outline' | 'dashed';
}) {
  const className =
    variant === 'primary'
      ? 'flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-4 text-sm font-bold text-white shadow-lg transition hover:scale-[1.02] hover:shadow-primary/20'
      : variant === 'dashed'
        ? 'flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary/30 bg-primary/5 py-3 text-xs font-bold text-primary'
        : 'flex w-full items-center justify-center rounded-xl border border-primary/20 py-3 text-sm font-bold text-primary';

  return (
    <Link to={to} className={className}>
      {variant !== 'outline' ? <LucideSymbolIcon name={icon} tone="inherit" size={16} /> : null}
      {label}
      {variant === 'primary' || variant === 'dashed' ? (
        <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
      ) : null}
    </Link>
  );
}

function StatRow({
  icon,
  label,
  helper,
  value,
  tone = 'primary',
}: {
  icon: string;
  label: string;
  helper: string;
  value: string;
  tone?: 'primary' | 'amber' | 'emerald';
}) {
  const iconTone =
    tone === 'amber' ? 'bg-amber-500/10 text-amber-600' : tone === 'emerald' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-primary/10 text-primary';
  const valueTone = tone === 'amber' ? 'text-amber-600' : tone === 'emerald' ? 'text-emerald-600' : 'text-primary';

  return (
    <div className="flex min-w-0 items-start gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${iconTone}`}>
        <LucideSymbolIcon name={icon} tone="inherit" size={22} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold">{label}</p>
        <p className="mt-1 break-words text-[10px] text-on-surface-variant [overflow-wrap:anywhere]">{helper}</p>
      </div>
      <span
        className={`max-w-[48%] shrink-0 break-words text-right text-xs font-bold tabular-nums [overflow-wrap:anywhere] ${valueTone}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

export default function AdminDashboardRail({ user, data }: Props) {
  const m10 = data.module10;
  const fin = m10?.financier ?? data.paiements;
  const reste = Math.max(0, data.montantTotalCdf - data.montantPayeCdf);

  const cards: ReactNode[] = [];

  if (canSeeDashboardModule(user, 'inscriptions')) {
    cards.push(
      <RailCard
        key="inscriptions"
        title="Inscriptions"
        footer={<RailActionLink to="/admin/inscriptions" label="Ouvrir les inscriptions" icon="assignment" variant="primary" />}
      >
        <div className="space-y-4">
          <StatRow
            icon="warning"
            label="En attente"
            helper="Paiement à relancer"
            value={String(data.inscriptionsEnAttentePaiement)}
            tone="amber"
          />
          <StatRow
            icon="check_circle"
            label="Confirmées"
            helper="Inscriptions validées"
            value={String(data.inscriptionsConfirmees)}
            tone="emerald"
          />
          <StatRow
            icon="assignment"
            label="Total"
            helper="Toutes inscriptions"
            value={String(data.inscriptionsTotal ?? 0)}
          />
        </div>
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'apprenants')) {
    cards.push(
      <RailCard
        key="apprenants"
        title="Apprenants"
        footer={<RailActionLink to="/admin/apprenants" label="Voir les apprenants" icon="group" variant="primary" />}
      >
        <StatRow
          icon="group"
          label="Apprenants actifs"
          helper="Acompte ou paiement complet"
          value={String(m10?.academique?.nombreApprenants ?? data.apprenantsTotal ?? 0)}
          tone="emerald"
        />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'formations')) {
    cards.push(
      <RailCard
        key="formations"
        title="Formations & cohortes"
        footer={<RailActionLink to="/admin/formations" label="Gérer les formations" icon="school" variant="primary" />}
      >
        <StatRow
          icon="school"
          label="Cohortes ouvertes"
          helper={
            (data.cohortesPleines ?? 0) > 0
              ? `${data.cohortesPleines} cohorte(s) complète(s)`
              : 'Statut OUVERTE'
          }
          value={String(data.cohortesOuvertes ?? 0)}
        />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'paiements')) {
    cards.push(
      <RailCard
        key="paiements"
        title="Résumé financier"
        footer={
          <div className="space-y-3">
            <RailActionLink to="/admin/paiements" label="Gérer les paiements" icon="payments" variant="primary" />
            {canSeeDashboardModule(user, 'tarifs') ? (
              <RailActionLink to="/admin/tarifs" label="Gérer les tarifs" icon="payments" variant="outline" />
            ) : null}
          </div>
        }
      >
        <div className="space-y-4">
          <StatRow
            icon="payments"
            label="Encaissé"
            helper="Confirmations payées"
            value={formatMoney(data.montantPayeCdf, 'CDF')}
          />
          <StatRow
            icon="warning"
            label="Impayés"
            helper={`${fin?.paiementsEnAttente ?? data.inscriptionsEnAttentePaiement} dossier(s)`}
            value={formatMoney(fin?.montantImpayeTotal ?? 0, 'CDF')}
            tone="amber"
          />
        </div>
        {fin ? (
          <div className="mt-4 space-y-2 rounded-lg bg-surface-container-low p-3 text-xs">
            <div className="flex min-w-0 justify-between gap-2">
              <span className="shrink-0 text-on-surface-variant">Recettes aujourd&apos;hui</span>
              <span className="min-w-0 break-words text-right font-bold text-primary tabular-nums [overflow-wrap:anywhere]">
                {formatMoney(fin.recettesJournalieres ?? 0, 'CDF')}
              </span>
            </div>
          </div>
        ) : null}
        <div className="mt-4 space-y-3 border-t border-surface-container pt-4">
          <div className="flex min-w-0 items-start justify-between gap-2 text-xs text-on-surface-variant">
            <span className="shrink-0">Reste à encaisser</span>
            <span className="min-w-0 break-words text-right tabular-nums [overflow-wrap:anywhere]">{formatMoney(reste, 'CDF')}</span>
          </div>
          <div className="flex min-w-0 items-start justify-between gap-2 text-lg font-bold">
            <span className="shrink-0">Total facturé</span>
            <span className="min-w-0 break-words text-right text-primary tabular-nums [overflow-wrap:anywhere]">
              {formatMoney(data.montantTotalCdf, 'CDF')}
            </span>
          </div>
        </div>
      </RailCard>,
    );
  } else if (canSeeDashboardModule(user, 'tarifs')) {
    cards.push(
      <RailCard key="tarifs" title="Tarification" footer={<RailActionLink to="/admin/tarifs" label="Gérer les tarifs" icon="payments" variant="primary" />}>
        <p className="text-sm text-on-surface-variant">Consultez et ajustez les tarifs des formations et prestations.</p>
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'parrainage')) {
    cards.push(
      <RailCard
        key="parrainage"
        title="Parrainage"
        footer={<RailActionLink to="/admin/parrainage" label="Ouvrir le parrainage" icon="groups" variant="primary" />}
      >
        <StatRow
          icon="card_membership"
          label="Bonus du mois"
          helper="Commissions distribuées"
          value={formatMoney(fin?.bonusDistribuesMois ?? 0, 'CDF')}
        />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'academique')) {
    cards.push(
      <RailCard
        key="academique"
        title="Académique"
        footer={<RailActionLink to="/admin/academique" label="Suivi académique" icon="menu_book" variant="primary" />}
      >
        <StatRow
          icon="check_circle"
          label="Taux de réussite"
          helper={`${m10?.academique?.inscriptionsReussies ?? 0} parcours aboutis`}
          value={m10?.academique?.tauxReussitePct != null ? `${m10.academique.tauxReussitePct} %` : '—'}
        />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'excellence')) {
    cards.push(
      <RailCard
        key="excellence"
        title="Excellence"
        footer={<RailActionLink to="/admin/excellence" label="Programme excellence" icon="emoji_events" variant="primary" />}
      >
        <StatRow
          icon="emoji_events"
          label="Classement"
          helper={`Shortlist ${m10?.academique?.classementShortlist ?? 0} / ${m10?.academique?.tailleShortlist ?? '—'}`}
          value={String(m10?.academique?.classementGeneralTotal ?? 0)}
        />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'formateurs')) {
    cards.push(
      <RailCard
        key="formateurs"
        title="Formateurs & RH"
        footer={<RailActionLink to="/admin/formateurs" label="Gérer les formateurs" icon="cast_for_education" variant="primary" />}
      >
        <div className="space-y-4">
          <StatRow
            icon="schedule"
            label="Heures prestées"
            helper="Mois en cours"
            value={`${m10?.rh?.heuresPresteesMois ?? 0} h`}
          />
          <StatRow
            icon="stars"
            label="Satisfaction"
            helper={`${m10?.rh?.nombreEvaluations ?? 0} évaluation(s)`}
            value={m10?.rh?.tauxSatisfactionPedagogiquePct != null ? `${m10.rh.tauxSatisfactionPedagogiquePct} %` : '—'}
          />
          <StatRow
            icon="account_balance_wallet"
            label="Rémunérations dues"
            helper={`Payées : ${formatMoney(m10?.rh?.remunerationsPayeesCdf ?? 0, 'CDF')}`}
            value={formatMoney(m10?.rh?.remunerationsDuCdf ?? 0, 'CDF')}
          />
        </div>
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'logistique')) {
    cards.push(
      <RailCard
        key="logistique"
        title="Logistique"
        footer={<RailActionLink to="/admin/logistique" label="Patrimoine & salles" icon="inventory_2" variant="primary" />}
      >
        <div className="space-y-4">
          <StatRow
            icon="inventory_2"
            label="Articles"
            helper="Patrimoine total"
            value={String(m10?.logistique?.totalArticles ?? 0)}
          />
          <StatRow
            icon="check_circle"
            label="Disponibles"
            helper="En stock"
            value={String(m10?.logistique?.equipementsDisponibles ?? 0)}
            tone="emerald"
          />
          <StatRow
            icon="warning"
            label="En panne"
            helper={`${m10?.logistique?.pannesOuvertes ?? 0} signalement(s)`}
            value={String(m10?.logistique?.equipementsEnPanne ?? 0)}
            tone="amber"
          />
        </div>
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'documents')) {
    cards.push(
      <RailCard key="documents" title="Dossiers" footer={<RailActionLink to="/admin/documents" label="Valider les dossiers" icon="folder_shared" variant="dashed" />}>
        <p className="text-sm text-on-surface-variant">Contrôlez les pièces et dossiers soumis par les apprenants.</p>
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'site')) {
    cards.push(
      <RailCard key="site" title="Site & QR">
        <PublicPlatformQrCard variant="admin" />
      </RailCard>,
    );
  }

  if (canSeeDashboardModule(user, 'settings')) {
    cards.push(
      <RailCard
        key="settings"
        title="Devise & taux"
        footer={<RailActionLink to="/admin/settings" label="Paramètres devise" icon="settings_suggest" variant="primary" />}
      >
        <p className="text-sm text-on-surface-variant">Taux de change CDF / USD et mode d&apos;affichage des montants.</p>
      </RailCard>,
    );
  }

  if (cards.length === 0) {
    return null;
  }

  return <div className="w-full shrink-0 space-y-6 lg:w-80">{cards}</div>;
}
